import { useState } from 'react';
import { MessageCircle, Mail, Send, MapPin, Clock } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { BRAND } from './content';
import { toast } from 'sonner';
import { trackEvent } from '../../lib/clarity';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', role: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill all required fields.');
      return;
    }
    setSending(true);
    trackEvent('contact_form_submitted', { role: form.role || 'unspecified' });
    // Open WhatsApp pre-filled with form contents (no backend dependency)
    const text = `Hi Founder,\n\nName: ${form.name}\nEmail: ${form.email}\nRole: ${form.role || 'N/A'}\n\n${form.message}`;
    window.open(BRAND.whatsappLink(text), '_blank');
    setTimeout(() => {
      setSending(false);
      toast.success('Opening WhatsApp — your message is pre-filled.');
      setForm({ name: '', email: '', role: '', message: '' });
    }, 300);
  };

  return (
    <MarketingLayout>
      <SEO
        title="Contact | WhatsApp the founder or email support"
        description="Talk to a human at ActanonEdu — WhatsApp the founder directly, or email support@actanonedu.com. India-hosted. We reply within 6 working hours on WhatsApp."
        path="/contact"
        keywords={['ActanonEdu contact', 'WhatsApp founder', 'school AI support India']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }]}
      />
      <PageHero
        eyebrow="Contact"
        title="Talk to a"
        accent="human."
        subtitle="Founder reads every message. No tickets. No bot deflection. Pick the channel that suits you."
        testid="contact-hero"
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <form onSubmit={submit} className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6 sm:p-8" data-testid="contact-form">
            <h2 className="text-xl font-bold mb-1">Send us a message</h2>
            <p className="text-sm text-slate-500 mb-6">We will open WhatsApp with your message pre-filled. You confirm and send.</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-mk-muted dark:text-slate-300">Name *</label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Your full name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-mk-muted dark:text-slate-300">Email *</label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="you@school.edu" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-mk-muted dark:text-slate-300">Role</label>
                <Input data-testid="contact-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1" placeholder="Teacher / Principal / Admin" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-mk-muted dark:text-slate-300">Message *</label>
                <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" rows={5} placeholder="What can we help with?" />
              </div>
              <Button type="submit" disabled={sending} className="w-full bg-mk-blue hover:bg-mk-deep text-white h-11 font-semibold" data-testid="contact-submit">
                <Send className="h-4 w-4 mr-1.5" /> {sending ? 'Opening WhatsApp...' : 'Send via WhatsApp'}
              </Button>
            </div>
          </form>

          {/* Channels */}
          <div className="space-y-4">
            <a
              href={BRAND.whatsappLink('Hi Founder! I came from the contact page.')}
              target="_blank" rel="noopener noreferrer"
              className="block rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/70 dark:bg-emerald-900/20 p-6 hover:shadow-lg transition-all"
              data-testid="contact-wa"
            >
              <MessageCircle className="h-6 w-6 text-emerald-700 dark:text-emerald-400 mb-2" />
              <div className="font-bold">WhatsApp the Founder</div>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">Fastest path to a human — tap to start a chat. Number shared after you tap.</p>
            </a>
            <a
              href={`mailto:${BRAND.supportEmail}`}
              className="block rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6 hover:shadow-lg transition-all"
              data-testid="contact-email-link"
            >
              <Mail className="h-6 w-6 text-mk-deep dark:text-mk-cyan mb-2" />
              <div className="font-bold">Email Support</div>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">{BRAND.supportEmail} — for account, billing, and policy questions.</p>
            </a>
            <div className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6">
              <div className="flex items-start gap-4">
                <Clock className="h-5 w-5 text-slate-400 mt-1" />
                <div>
                  <div className="font-bold text-sm">Response time</div>
                  <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">WhatsApp · within 6 working hours.<br />Email · within 24 working hours.</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6">
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-slate-400 mt-1" />
                <div>
                  <div className="font-bold text-sm">Headquartered in India</div>
                  <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">All servers in India · GDPR-style data dignity defaults.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
